
import * as Tone from 'tone';
import _ from 'lodash'
import { randomBool, numberRange } from '../../utils/helpers'
import * as Note from '@tonaljs/note'

const SAMPLE_LOAD_TIMEOUT_MS = 20000

export default class InstrumentBaseClass {
    constructor (name, articulations, folder) {
        this.id = Math.floor(Math.random() * 999999)
        this.parameters = {}
        this.name = name
        this.articulations = articulations
        this.folder = folder
        this.instrument = null
    }
    dispose () {
        if (!_.isNil(this.instrument) && !_.isNil(this.instrument._context)) {
            this.instrument.releaseAll()
            this.instrument.dispose()
        }
        this.instrument = null
        this.clearPart()
    }
    updateParameter (parameter, value) {
        this.parameters[parameter] = value
    }
    updateParameters (parameters) {
        let _this = this
        Object.keys(parameters).forEach(function (key) {
            if (key !== 'id') {
                _this.updateParameter(key, parameters[key])
            }
        })
    }
    connect (channel) {
        this.connectedToChannel = channel
        this.instrument.connect(this.connectedToChannel)
    }
    /** Rejects when the articulation is unknown or its samples cannot be fetched. */
    async load (articulationId) {
        const articulation = this.articulations[articulationId]
        if (_.isNil(articulation)) {
            throw new Error(`Unknown articulation "${articulationId}" for instrument ${this.name}`)
        }
        this.parameters.articulation = articulationId
        const sampleMap = this.getSampleMap(articulation)
        this.sampleMap = _.cloneDeep(sampleMap)
        if (!_.isNil(sampleMap)) {
            await this.loadSamples(sampleMap)
        }
    }
    /**
     * Creates the sampler and settles once every buffer has loaded, or rejects on the first
     * failed buffer or after SAMPLE_LOAD_TIMEOUT_MS. Before this a single missing file kept the
     * whole round on the loading spinner forever.
     */
    loadSamples (sampleMap) {
        const _this = this
        this.dispose()
        return new Promise(function (resolve, reject) {
            let settled = false
            const finish = (error) => {
                if (settled) return
                settled = true
                clearTimeout(timer)
                if (error) {
                    reject(error)
                } else {
                    resolve()
                }
            }
            const timer = setTimeout(() => {
                finish(new Error(`Timed out loading samples for instrument ${_this.name}`))
            }, SAMPLE_LOAD_TIMEOUT_MS)
            try {
                _this.instrument = new Tone.Sampler(sampleMap, {
                    onload: function () {
                        _this.updateParameters(_this.parameters)
                        if (!_.isNil(_this.connectedToChannel) && !_.isNil(_this.instrument)) {
                            _this.instrument.connect(_this.connectedToChannel)
                        }
                        _this.loaded()
                        finish()
                    },
                    onerror: function (error) {
                        finish(error instanceof Error ? error : new Error(`Could not load samples for instrument ${_this.name}`))
                    }
                })
            } catch (e) {
                finish(e)
            }
        })
    }
    loaded () {
        // to be overidden
    }
    loadPart (
        notes, numberOfBars
    ) {
        let _this = this
        this.clearPart()
        this.notes = _.cloneDeep(notes)
        this.beforeLoadPart(this.notes)
        for (let note of this.notes) {
            note.time += 'i';
            note.duration += 'i';
            note.midi = this.calculateMidiNoteFromVelocity(note.velocity)
        }
        this.part = new Tone.Part(function (time, note) {
            if (
                !_.isNil(_this.instrument) &&
                !_.isNil(_this.instrument.context)
            ) {
                let shouldPlayNote = true
                if (note.probability < 1) {
                    shouldPlayNote = randomBool(note.probability)
                }
                if (shouldPlayNote) {
                    // attack only: a drum hit plays to the end of its sample. The release that used to
                    // come after one step cut every hit at the next step (125 ms at 120 bpm, then the
                    // sampler's 100 ms fade) on samples of 0.6 to 2.2 s, which is what a busy round
                    // sounded like. The note keeps its duration for the automation, which reads it.
                    _this.instrument.triggerAttack(
                        Tone.Midi(note.midi),
                        time,
                        note.velocity
                    )
                }
            }
        }, this.notes)
        this.afterPartLoaded()
        this.part.loop = true
        this.part.loopEnd = numberOfBars + ":0:0"
        this.part.start(0)
    }
    beforeLoadPart (notes) {
        // to be overidden if necessary
    }
    afterPartLoaded () {
        // to be overidden if necessary
    }
    getNotes () {
        return this.notes
    }
    clearPart () {
        if (!_.isNil(this.part) && !_.isNil(this.part._events)) {
            this.part.dispose()
        }
    }
    releaseAll (time) {
        if (!_.isNil(this.instrument)) {
            this.instrument.releaseAll(time)
        }
    }
    getSampleMap (articulation) {
        let map = {}
        let sampleIndex = 0
        for (let sample of articulation.samples) {
            map['C' + sampleIndex++] = '/samples/' + this.folder + '/' + sample.sample
        }
        /*let map = {
            'C0': '/samples/' + this.folder + '/' + articulation.samples[0].sample,
            'C1': '/samples/' + this.folder + '/' + articulation.samples[1].sample
        }*/
        return map
    }
    /* getSampleMap () {
         let map = {
             'C4': '/samples/' + this.folder + '/' + this.parameters.articulation
         }
         return map
     }*/
    calculateMidiNoteFromVelocity (velocity) {
        // based on this.parameters.articulation.samples calculate midi note that coresponds with velocity
        //return 12 // default 'C0' as midi note 12 ?
        let midiVelocity = numberRange(velocity, 0, 1, 0, 127)

        const articulation = this.articulations[this.parameters.articulation]
        let sampleIndex = _.findIndex(articulation.samples, (articulation) => {
            return (midiVelocity >= articulation.lovel) && (midiVelocity <= articulation.hivel)
        })
        return (sampleIndex + 1) * 12
    }
    triggerNote (note) {
        this.instrument.triggerAttackRelease(
            Note.fromMidi(note.midi),
            note.duration + 'i',
            undefined,
            note.velocity
        )
    }
    triggerAttack (pitch, velocity) {
        this.instrument.triggerAttack(Note.fromMidi(pitch), undefined, velocity)
    }
    triggerRelease (pitch) {
        this.instrument.triggerRelease(Note.fromMidi(pitch))
    }
}
